<template lang="pug">
.analytics__queue
    v-container(fluid)
        v-card(light)
            v-card-text
                v-row(align="center")
                    v-col(cols="12" md="4" lg="3")
                        v-text-field(v-model="search" label="Search queue" clearable color="brand" hide-details)
                    v-col(cols="auto")
                        v-btn(
                            @click="fetchQueue"
                            color="brand"
                            small
                            class="white--text"
                            :disabled="isRefreshing || isAutoRefresh"
                        ) Refresh
                    v-col(cols="auto")
                        v-tooltip(top)
                            template(v-slot:activator="{ on, attrs }")
                                v-btn(
                                    v-bind="attrs"
                                    v-on="on"
                                    :color="isAutoRefresh ? 'red' : 'green'"
                                    small
                                    class="white--text"
                                    @click="toggleAutoRefresh"
                                )
                                    | {{ isAutoRefresh ? 'Stop Auto-Refresh' : 'Start Auto-Refresh' }}
                            span The refresh rate is 30 seconds
                    v-col(cols="12" md="4" lg="3")
                        v-menu(
                            ref="menu"
                            v-model="menu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                            color="brand"
                        )
                            template(v-slot:activator="{ on, attrs }")
                                v-text-field(
                                    v-model="refreshStartDate"
                                    label="Select Start Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    hide-details
                                    v-bind="attrs"
                                    v-on="on"
                                )
                            v-date-picker(
                                light
                                v-model="refreshStartDate"
                                @input="menu = false"
                                color="brand"
                                elevation="3"
                                show-current
                                header-color="brand"
                                :max="new Date().toISOString().split('T')[0]"
                                :show-week="true"
                            )
                    v-col(cols="12" md="auto")
                        span(class="last-refreshed queue-toolbar__timestamp")
                            | Last Refreshed: {{ lastRefreshed }}

                v-alert(v-if="error" type="error" dense outlined class="mb-4") {{ error }}

                .queue-summary(v-if="queueSummary.length")
                    v-chip(
                        v-for="summary in queueSummary"
                        :key="summary.status"
                        small
                        dark
                        :color="getStatusColor(summary.status)"
                        class="mr-2 mb-2"
                    )
                        | {{ summary.status }}: {{ summary.count }}

                .queue-empty(v-if="!groupedQueueItems.length && !isRefreshing")
                    v-icon(color="grey") mdi-database-search-outline
                    p.queue-empty__title No queue items match the current filters.
                    p.queue-empty__text Adjust the search or start date to widen the result set.

                .queue-groups(v-else)
                    section.queue-group(v-for="group in groupedQueueItems" :key="group.status")
                        .queue-group__heading
                            .queue-group__title-wrap
                                h3.queue-group__title {{ group.status }}
                                span.queue-group__subtitle {{ group.items.length }} item{{ group.items.length === 1 ? '' : 's' }}
                        v-expansion-panels(flat multiple class="queue-panels")
                            v-expansion-panel(
                                v-for="item in group.items"
                                :key="item.id"
                                :class="['queue-card', `queue-card--${(item.status || 'unknown').toLowerCase()}`]"
                            )
                                v-expansion-panel-header(class="queue-card__header")
                                    .queue-card__summary
                                        .queue-card__line.queue-card__line--primary
                                            .queue-card__identity
                                                v-chip.queue-card__status(
                                                    small
                                                    dark
                                                    :color="getStatusColor(item.status)"
                                                ) {{ item.status }}
                                                span.queue-card__task {{ item.taskInfo?.task_name || 'Unknown Task' }}
                                                span.queue-card__customer(v-if="item.system?.customer?.name") {{ item.system.customer.name }}
                                        .queue-card__line.queue-card__line--secondary
                                            span.queue-card__meta Q# {{ item.id }}
                                            span.queue-card__meta(v-if="item.task_id") Task {{ item.task_id }}
                                            span.queue-card__meta(v-if="item.system_id") {{ item.system_id }}
                                            span.queue-card__meta Attempts {{ item.attempts_count || 1 }}
                                            span.queue-card__meta(v-if="item.updatedAt") Updated {{ formatRelativeDate(item.updatedAt) }}
                                            span.queue-card__meta(v-if="item.user") User {{ formatUserEmail(item.user) }}
                                            .todd(v-if="item.error")
                                                span.queue-card__error-label Error
                                                span.queue-card__error-text {{ item.error }}

                                v-expansion-panel-content
                                    .queue-card__details
                                        v-row
                                            v-col(cols="12" md="6")
                                                v-card.queue-history-card(flat)
                                                    v-card-title.queue-history-card__title Task History
                                                    v-card-subtitle.queue-history-card__subtitle
                                                        | Q# {{ item.id }}
                                                        span(v-if="item.system_id")  · {{ item.system_id }}
                                                        span(v-if="getTaskHistoryElapsed(item.queueStatusHistory)")  · Elapsed {{ getTaskHistoryElapsed(item.queueStatusHistory) }}
                                                    v-card-text.queue-history-card__content
                                                        .queue-history-empty(v-if="!item.queueStatusHistory || !item.queueStatusHistory.length")
                                                            | No history available.
                                                        .queue-history-list(v-else)
                                                            .queue-history-entry(
                                                                v-for="historyItem in formatTaskHistory(item.queueStatusHistory)"
                                                                :key="historyItem.id || `${item.id}-${historyItem.createdAt}-${historyItem.new_status}`"
                                                            )
                                                                .queue-history-entry__top
                                                                    span.queue-history-entry__status {{ historyItem.old_status || 'Unknown' }}
                                                                    v-icon.queue-history-entry__arrow(medium) arrow_right
                                                                    span.queue-history-entry__status {{ historyItem.new_status || 'Unknown' }}
                                                                    span.queue-history-entry__time(v-if="historyItem.createdAtFormatted") {{ historyItem.createdAtFormatted }}
                                                                .queue-history-entry__body(v-if="historyItem.message") {{ historyItem.message }}
                                                                .queue-history-entry__meta(v-if="historyItem.user")
                                                                    | {{ formatUserEmail(historyItem.user) }}
</template>

<script>

import {DateTime} from 'luxon';

export default {
    meta: {
        role: 'analytics-queue'
    },
    emits: [],
    data () {
        return {
            search: '',
            queueItems: [],
            error: '',
            isAutoRefresh: false,
            autoRefreshInterval: null,
            deleteDialog: false,
            taskToEdit: {},
            editDialog: false,
            allPossibleStatuses: [
                'PENDING',
                'COMPLETED',
                'FAILED',
                'EXPIRED',
                'WAITING',
                'REPLACED'
            ],
            statusOrder: [
                'FAILED',
                'PENDING',
                'WAITING',
                'IN_PROGRESS',
                'EXPIRED',
                'REPLACED',
                'COMPLETED'
            ],
            menu: false,
            refreshStartDate: DateTime.now().minus({ days: 1 }).toISODate(),
            lastRefreshed: DateTime.now().toFormat('MM/dd hh:mm:ss a ZZZZ'),
            isRefreshing: false,
        };
    },
    computed: {
        filteredQueueItems () {
            const search = this.search?.trim().toLowerCase();
            if (!search) {
                return this.queueItems;
            }

            return this.queueItems.filter(item => {
                const searchValue = [
                    item.id,
                    item.task_id,
                    item.system?.customer?.name,
                    item.system_id,
                    item.taskInfo?.task_name,
                    item.status,
                    item.message,
                    item.error,
                    item.user,
                    this.formatUserEmail(item.user),
                ].filter(Boolean).join(' ').toLowerCase();

                return searchValue.includes(search);
            });
        },
        groupedQueueItems () {
            return this.statusOrder
                .map(status => ({
                    status,
                    items: this.filteredQueueItems.filter(item => item.status === status)
                }))
                .filter(group => group.items.length);
        },
        queueSummary () {
            return this.groupedQueueItems.map(group => ({
                status: group.status,
                count: group.items.length,
            }));
        },
        statusItems () {
            if (this.taskToEdit?.status === 'WAITING') {
                return [];
            }
            return this.allPossibleStatuses.filter(status => status !== this.taskToEdit?.status && status !== 'WAITING');
        }
    },
    watch: {
        refreshStartDate (newVal) {
            if (newVal) {
                this.fetchQueue();
            }
        }
    },
    mounted () {
        this.fetchQueue();
    },
    methods: {
        fetchQueue () {
            this.isRefreshing = true;
            this.socketEmit('analyticsTaskQueue', {startDate: this.refreshStartDate}, ({queue, error}) => {
                if (error) {
                    this.$set(this, 'error', error);
                    console.error('Error fetching queue items:', error);
                } else {
                    this.$set(this, 'queueItems', queue);
                    this.$set(this, 'error', '');
                    this.lastRefreshed = DateTime.now().toFormat('M/dd hh:mm:ss a ZZZZ');
                    const refreshedElement = this.$el.querySelector('.last-refreshed');
                    if (refreshedElement) {
                        refreshedElement.classList.add('refresh-highlight');
                        setTimeout(() => {
                            refreshedElement.classList.remove('refresh-highlight');
                        }, 1000);
                    }
                }
                this.isRefreshing = false;
            });
        },

        editTask (task) {
            this.taskToEdit = task;
            this.editDialog = true;
        },

        toggleAutoRefresh () {
            if (this.isAutoRefresh) {
                clearInterval(this.autoRefreshInterval);
                this.autoRefreshInterval = null;
                this.isAutoRefresh = false;
            } else {
                this.autoRefreshInterval = setInterval(this.fetchQueue, 30000);
                this.isAutoRefresh = true;
            }
        },

        getStatusColor (status) {
            const colors = {
                PENDING: 'grey',
                IN_PROGRESS: 'blue',
                COMPLETED: 'green',
                FAILED: 'red',
                EXPIRED: 'orange',
                WAITING: 'teal',
                REPLACED: 'purple',
            };
            return colors[status] || 'grey';
        },

        formatDateTime (dateString) {
            if (!dateString) {
                return '';
            }
            return DateTime.fromISO(dateString).toLocal().toFormat('M/dd hh:mm:ss a ZZZZ');
        },

        formatRelativeDate (dateString) {
            if (!dateString) {
                return '';
            }
            return DateTime.fromISO(dateString).toRelative() || this.formatDateTime(dateString);
        },

        formatUserEmail (email) {
            if (!email) {
                return '';
            }
            return email.split('@')[0];
        },

        formatTaskHistory (history) {
            return history.map(task => ({
                ...task,
                createdAtFormatted: task.createdAt
                    ? DateTime.fromISO(task.createdAt).toLocal().toFormat('MM/dd hh:mm:ss a ZZZZ')
                    : '',
            }));
        },

        getTaskHistoryElapsed (history) {
            if (!Array.isArray(history) || history.length === 0) {
                return '';
            }

            const historyDates = history
                .filter(entry => entry?.new_status !== 'PENDING')
                .map(entry => entry?.createdAt)
                .filter(Boolean)
                .map(dateString => DateTime.fromISO(dateString));

            if (!historyDates.length) {
                return '';
            }

            const validDates = historyDates.filter(date => date.isValid);

            if (!validDates.length) {
                return '';
            }

            const earliest = validDates.reduce((minDate, currentDate) => {
                return currentDate.toMillis() < minDate.toMillis() ? currentDate : minDate;
            });
            const latest = validDates.reduce((maxDate, currentDate) => {
                return currentDate.toMillis() > maxDate.toMillis() ? currentDate : maxDate;
            });

            return this.formatDuration(earliest, latest);
        },

        formatDuration (startDate, endDate) {
            const duration = endDate.diff(startDate, ['days', 'hours', 'minutes', 'seconds'])
                .shiftTo('days', 'hours', 'minutes', 'seconds')
                .normalize();

            if (!duration.isValid) {
                return '';
            }

            const days = Math.floor(duration.days || 0);
            const hours = Math.floor(duration.hours || 0);
            const minutes = Math.floor(duration.minutes || 0);
            const seconds = Math.floor(duration.seconds || 0);

            const parts = [];

            if (days) {
                parts.push(`${days}d`);
            }
            if (hours || parts.length) {
                parts.push(`${hours}h`);
            }
            if (minutes || parts.length) {
                parts.push(`${minutes}m`);
            }
            parts.push(`${seconds}s`);

            return parts.join(' ');
        },
    }
};
</script>

<style scoped>
.refresh-highlight {
    background-color: yellow;
    transition: background-color 1s ease-out;
}

.v-container {
    max-width: 1180px;
}

.queue-toolbar__timestamp {
    display: inline-block;
    font-size: 0.9em;
}

.queue-summary {
    margin-top: 16px;
    margin-bottom: 16px;
}

.queue-groups {
    display: flex;
    flex-direction: column;
    font-family: 'Segoe UI', 'Inter', 'Roboto', system-ui, sans-serif;
    gap: 18px;
}

.queue-group__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.queue-group__title-wrap {
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.queue-group__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.queue-group__subtitle {
    color: var(--v-brand-base);
    font-size: 0.85rem;
}

.queue-panels {
    background: transparent;
}

.queue-card {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(247, 250, 252, 0.9));
    border: 1px solid rgba(11, 31, 52, 0.12);
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(11, 31, 52, 0.06);
    margin-bottom: 6px;
    overflow: hidden;
    position: relative;
    transition: box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease;
}

.queue-card::before {
    background: #7b8794;
    content: '';
    height: 100%;
    left: 0;
    opacity: 0.9;
    position: absolute;
    top: 0;
    width: 4px;
    z-index: 1;
}

.queue-card:hover {
    border-color: rgba(11, 31, 52, 0.24);
    box-shadow: 0 14px 30px rgba(11, 31, 52, 0.12);
    transform: translateY(-1px);
}

.queue-card--failed::before {
    background: #c53030;
}

.queue-card--pending::before {
    background: #4a5568;
}

.queue-card--waiting::before {
    background: #0d9488;
}

.queue-card--in_progress::before {
    background: #2563eb;
}

.queue-card--completed::before {
    background: #2f855a;
}

.queue-card--expired::before {
    background: #dd6b20;
}

.queue-card--replaced::before {
    background: #805ad5;
}

.queue-card__header {
    backdrop-filter: saturate(1.1);
    border-bottom: 1px solid rgba(11, 31, 52, 0.08);
    padding-top: 10px;
    padding-bottom: 10px;
    position: relative;
    transition: background-color 180ms ease;
    z-index: 2;
}

.queue-card__header.v-expansion-panel-header--active {
    background: rgba(11, 31, 52, 0.04);
}

.queue-card__summary {
    animation: queue-enter 220ms ease;
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
}

.queue-card__line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
}

.queue-card__identity {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex-wrap: wrap;
}

.queue-card__status {
    flex-shrink: 0;
}

.queue-card__task {
    color: var(--v-brand-base);
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.01em;
}

.queue-card__customer {
    color: var(--v-brand-base);
    font-size: 0.9rem;
}

.queue-card__line--secondary {
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 8px 14px;
}

.queue-card__meta {
    color: var(--v-brand-base);
    font-size: 0.84rem;
    font-weight: 600;
}

.queue-card__error-label {
    background: #8b1e1e;
    border-radius: 3px;
    color: #fff;
    flex-shrink: 0;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    padding: 2px 6px;
    text-transform: uppercase;
}

.queue-card__error-text {
    color: var(--v-brand-base);
    font-size: 0.84rem;
    line-height: 1.4;
    white-space: normal;
    word-break: break-word;
}

.queue-card__details {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding-top: 12px;
}

@keyframes queue-enter {
    from {
        opacity: 0;
        transform: translateY(2px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.queue-history-card {
    background: rgba(0, 0, 0, 0.02);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    height: 100%;
}

.queue-history-card__title {
    font-size: 0.9rem;
    font-weight: 700;
    min-height: 0;
    padding: 12px 12px 4px;
}

.queue-history-card__subtitle {
    color: var(--v-brand-base);
    font-size: 0.8rem;
    padding: 0 12px;
}

.queue-history-card__content {
    padding: 10px 12px 12px;
}

.queue-history-empty {
    color: var(--v-brand-base);
    font-size: 0.9rem;
}

.queue-history-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 260px;
    overflow: auto;
}

.queue-history-entry {
    background: #fff;
    border: 1px solid rgba(11, 31, 52, 0.1);
    border-radius: 10px;
    box-shadow: 0 4px 10px rgba(11, 31, 52, 0.06);
    padding: 8px 10px;
}

.queue-history-entry__top {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.queue-history-entry__status {
    font-size: 0.78rem;
    font-weight: 700;
}

.queue-history-entry__arrow {
    color: var(--v-brand-base);
}

.queue-history-entry__time {
    color: var(--v-brand-base);
    font-size: 0.76rem;
    margin-left: auto;
}

.queue-history-entry__body {
    color: var(--v-brand-base);
    font-size: 0.86rem;
    line-height: 1.4;
    margin-top: 6px;
    white-space: pre-wrap;
    word-break: break-word;
}

.queue-history-entry__meta {
    color: var(--v-brand-base);
    font-size: 0.76rem;
    margin-top: 6px;
}

.queue-empty {
    align-items: center;
    color: var(--v-brand-base);
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 220px;
    text-align: center;
}

.queue-empty__title {
    color: var(--v-brand-base);
    font-size: 1rem;
    font-weight: 600;
    margin: 12px 0 6px;
}

.queue-empty__text {
    margin: 0;
    max-width: 420px;
}

@media (max-width: 960px) {
    .queue-card:hover {
        transform: none;
    }

    .queue-card__line--primary {
        align-items: flex-start;
        flex-direction: column;
    }

    .queue-history-entry__time {
        margin-left: 0;
    }
}
</style>
